// Menu behavior for grid action columns.
//
// Renders a trigger button in the cell. On click, shows a floating menu
// built from server-rendered HTML in the row's `__actionsHtml`.
//
// The server provides the full menu markup via a data-slot. This behavior
// only handles the trigger button, positioning, and dismiss logic.
//
// Column def:  { field: "__actions__", behavior: "menu" }
//
// ERB template:
//   <div data-slot="actions">
//     <ul class="menu ...">
//       <li><a href="...">View</a></li>
//     </ul>
//   </div>

// Shared menu element — only one menu open at a time
let activeMenu = null
let activeCleanup = null

function dismissMenu() {
  if (activeMenu) {
    activeMenu.remove()
    activeMenu = null
  }
  if (activeCleanup) {
    activeCleanup()
    activeCleanup = null
  }
}

// Dismiss before Turbo caches the page, otherwise the menu gets
// baked into the snapshot and appears stuck open on back-navigation.
document.addEventListener("turbo:before-cache", dismissMenu)

function showMenu(trigger, html) {
  dismissMenu()

  const wrapper = document.createElement("div")
  wrapper.style.cssText = "position:fixed;z-index:9999;"
  wrapper.innerHTML = html

  // Position relative to the trigger button
  const rect = trigger.getBoundingClientRect()
  wrapper.style.top = `${rect.bottom + 4}px`
  wrapper.style.right = `${window.innerWidth - rect.right}px`

  document.body.appendChild(wrapper)
  activeMenu = wrapper

  // Dismiss on outside click, scroll, or escape
  const onDismiss = (e) => {
    if (e.type === "click" && wrapper.contains(e.target)) return
    dismissMenu()
  }
  const onKeydown = (e) => {
    if (e.key === "Escape") dismissMenu()
  }

  // Use setTimeout so the current click event doesn't immediately dismiss
  setTimeout(() => {
    document.addEventListener("click", onDismiss)
    document.addEventListener("scroll", onDismiss, true)
    document.addEventListener("keydown", onKeydown)
  }, 0)

  activeCleanup = () => {
    document.removeEventListener("click", onDismiss)
    document.removeEventListener("scroll", onDismiss, true)
    document.removeEventListener("keydown", onKeydown)
  }
}

// Inline SVG for the ellipsis-vertical icon (heroicon mini)
const TRIGGER_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4">
  <path d="M10 3a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM10 8.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM11.5 15.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z"/>
</svg>`

export const menu = {
  apply() {
    return {
      cellRenderer: (params) => {
        const html = params.data?.__actionsHtml
        if (!html || params.node.rowPinned) return ""

        const container = document.createElement("div")
        container.className = "flex items-center justify-center h-full"

        const trigger = document.createElement("button")
        trigger.className = "btn btn-ghost btn-square btn-xs"
        trigger.innerHTML = TRIGGER_ICON
        trigger.addEventListener("click", (e) => {
          e.stopPropagation()
          showMenu(trigger, html)
        })

        container.appendChild(trigger)
        return container
      },
      editable: false,
      sortable: false,
      filter: false,
      resizable: false,
      suppressNavigable: true,
      suppressHeaderMenuButton: true
    }
  }
}
